'use es6';

import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { debounce, bindAll } from 'underscore';
import shallowCompare from 'react-addons-shallow-compare';
import { getPage } from 'simple-menus-core/selectors/pages';
import { getMenuNode } from 'simple-menus-core/selectors/menuNode';
import { getMenuTreeNode } from 'simple-menus-core/selectors/menuTree';
import MenuNode from 'simple-menus-core/models/MenuNode';
import * as MenuNodeActions from 'simple-menus-core/actions/MenuNode';
import * as MenuTreeActions from 'simple-menus-core/actions/MenuTree';
import { DEFAULT_DEBOUNCE_TIME } from 'simple-menus-core/constants/constants';
import Routes from 'ContentUtils/Routes';
import StatesStatusGroups from 'ContentUtils/constants/StateStatusGroups';
import { byName } from 'ContentUtils/constants/TemplateTypes';
import { jsx as _jsx } from "react/jsx-runtime";
function makeMapStateToProps() {
  return function mapStateToProps(state, props) {
    const menuNode = getMenuNode(state, props);
    const pageLinkId = menuNode ? menuNode.pageLinkId || menuNode.contentGroupId : null;
    return {
      menuNode,
      page: menuNode ? getPage(state, {
        pageLinkId
      }) : {},
      pageLinkId,
      menuTreeNode: getMenuTreeNode(state, props)
    };
  };
}
const mapDispatchToProps = {
  updateNode: MenuNodeActions.updateNode,
  updatePageLinkId: MenuNodeActions.updatePageLinkId,
  replaceNode: MenuNodeActions.replaceNode,
  addChildNode: MenuTreeActions.addChildNode,
  addNode: MenuTreeActions.addNode,
  cloneNode: MenuTreeActions.cloneNode,
  deleteNode: MenuTreeActions.deleteNode
};
const HOC = ConnectedMenuOptions => {
  class WithConnectedMenuOptions extends Component {
    constructor(props) {
      super(props);
      bindAll(this, ['onPageLinkIdChange', 'addChild', 'addNodeBelow', 'cloneNode', 'replaceNode', 'deleteNode', 'setLinkExternal', 'onLinkUrlChange', 'onLinkParamsChange']);
      this.updateLinkUrl = debounce(this.updateLinkUrl.bind(this), DEFAULT_DEBOUNCE_TIME);
      this.updateLinkParams = debounce(this.updateLinkParams.bind(this), DEFAULT_DEBOUNCE_TIME);
    }
    shouldComponentUpdate(nextProps, nextState) {
      if (!nextProps.menuNode || !nextProps.menuTreeNode) return false;
      return shallowCompare(this, nextProps, nextState);
    }

    /* Update functions */
    updateLinkUrl(linkUrl) {
      this.props.updateNode(this.props.id, {
        linkUrl
      });
    }
    onLinkUrlChange({
      target: {
        value
      }
    }) {
      this.updateLinkUrl(value);
    }
    updateLinkParams(linkParams) {
      this.props.updateNode(this.props.id, {
        linkParams
      });
    }
    onLinkParamsChange({
      target: {
        value
      }
    }) {
      this.updateLinkParams(value);
    }
    updatePageLinkId(pageLinkId) {
      this.props.updatePageLinkId(this.props.id, pageLinkId);
    }
    onPageLinkIdChange(pageId) {
      this.updatePageLinkId(pageId);
    }

    /* Actions dropdown functions */
    addChild() {
      this.props.addChildNode(this.props.id, this.props.menuId);
    }
    addNodeBelow() {
      this.props.addNode(this.props.menuId, this.props.parentId, this.props.id);
    }
    cloneNode() {
      this.props.cloneNode(this.props.parentId, this.props.id, this.props.menuId);
    }
    replaceNode(type) {
      this.props.replaceNode(this.props.id, type);
    }
    deleteNode() {
      this.props.deleteNode(this.props.id, this.props.menuId);
    }
    setLinkExternal(external = true) {
      const linkTarget = external ? '_blank' : null;
      this.props.updateNode(this.props.id, {
        linkTarget
      });
    }
    get editorUrl() {
      const {
        page
      } = this.props;
      if (!page) return null;
      if (page.categoryId === byName.blog.categoryId) return Routes.blogDashboard(page.id);
      return Routes.edit(page.id, page.categoryId);
    }
    get isPublished() {
      const {
        page,
        menuNode
      } = this.props;
      if (!page) return menuNode.isPublished;
      return StatesStatusGroups.PUBLISHED.includes(page.state);
    }
    render() {
      const {
        id,
        isVisible,
        menuNode,
        menuTreeNode,
        page,
        pageLinkId,
        readOnly
      } = this.props;
      return /*#__PURE__*/_jsx(ConnectedMenuOptions, {
        addChild: this.addChild,
        addNodeBelow: this.addNodeBelow,
        cloneNode: this.cloneNode,
        deleteNode: this.deleteNode,
        editorUrl: this.editorUrl,
        hasChildren: !!menuTreeNode.children.length,
        id: id,
        isExternal: menuNode.linkTarget === '_blank',
        isPublished: this.isPublished,
        isVisible: isVisible,
        linkLabel: menuNode.linkLabel,
        linkUrl: menuNode.linkUrl,
        linkParams: menuNode.linkParams,
        onLinkParamsChange: this.onLinkParamsChange,
        onLinkUrlChange: this.onLinkUrlChange,
        onPageLinkIdChange: this.onPageLinkIdChange,
        pageLinkId: pageLinkId,
        pageLinkName: page && page.name || menuNode.pageLinkName,
        readOnly: readOnly,
        replaceNode: this.replaceNode,
        setLinkExternal: this.setLinkExternal,
        type: menuNode.type
      });
    }
  }
  WithConnectedMenuOptions.defaultProps = {
    menuNode: new MenuNode({})
  };
  WithConnectedMenuOptions.propTypes = {
    addChildNode: PropTypes.func.isRequired,
    addNode: PropTypes.func.isRequired,
    cloneNode: PropTypes.func.isRequired,
    deleteNode: PropTypes.func.isRequired,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    isVisible: PropTypes.bool.isRequired,
    menuId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    menuNode: PropTypes.instanceOf(MenuNode).isRequired,
    menuTreeNode: PropTypes.object,
    page: PropTypes.object,
    pageLinkId: PropTypes.number,
    parentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    readOnly: PropTypes.bool,
    replaceNode: PropTypes.func.isRequired,
    updateNode: PropTypes.func.isRequired,
    updatePageLinkId: PropTypes.func.isRequired
  };
  return connect(makeMapStateToProps, mapDispatchToProps)(WithConnectedMenuOptions);
};
export default function withConnectedMenuOptions() {
  return HOC;
}